import { Button, Card } from '@evw/ui';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import { dateInTimeZone } from '../../lib/date';
import {
  cancelManagedBooking,
  getManagedBooking,
  getPublicAvailability,
  rescheduleManagedBooking,
  type PublicSlot
} from './api';
import { PaymentPanel } from './PaymentPanel';

function initialToken() {
  const fromFragment = new URLSearchParams(window.location.hash.slice(1)).get('token');
  if (fromFragment) {
    sessionStorage.setItem('evw-booking-token', fromFragment);
    history.replaceState(null, '', '/manage');
  }
  return fromFragment ?? sessionStorage.getItem('evw-booking-token') ?? '';
}

export function BookingManagementPage() {
  const queryClient = useQueryClient();
  const [token] = useState(initialToken);
  const [date, setDate] = useState('');
  const [slots, setSlots] = useState<PublicSlot[]>([]);
  const [error, setError] = useState('');
  const booking = useQuery({
    queryKey: ['managed-booking', token],
    queryFn: () => getManagedBooking(token),
    enabled: Boolean(token),
    retry: false,
    refetchInterval: (query) => {
      const current = query.state.data;
      return current?.status === 'awaiting_payment' || current?.payment.state === 'processing'
        ? 3000
        : false;
    }
  });
  const selectedDate =
    date ||
    dateInTimeZone(
      booking.data?.location.timeZone ?? Intl.DateTimeFormat().resolvedOptions().timeZone
    );

  async function cancel() {
    if (!token) return;
    setError('');
    try {
      await cancelManagedBooking(token, 'Customer cancellation');
      await queryClient.invalidateQueries({ queryKey: ['managed-booking', token] });
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Cancellation failed');
    }
  }
  async function find(event: FormEvent) {
    event.preventDefault();
    if (!booking.data) return;
    setError('');
    try {
      const result = await getPublicAvailability({
        slug: booking.data.business.slug,
        locationId: booking.data.location.id,
        serviceId: booking.data.service.id,
        date: selectedDate
      });
      setSlots(result.slots.filter((item) => item.local_starts_at.startsWith(selectedDate)));
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Unable to load times');
    }
  }
  async function move(slot: PublicSlot) {
    try {
      await rescheduleManagedBooking(token, slot);
      setSlots([]);
      await queryClient.invalidateQueries({ queryKey: ['managed-booking', token] });
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Reschedule failed');
    }
  }

  if (!token)
    return (
      <main className="public-shell">
        <Card>
          <h1>Manage a booking</h1>
          <p>Open the secure management link supplied when the booking was created.</p>
          <a href="/">Return</a>
        </Card>
      </main>
    );
  if (booking.isLoading)
    return (
      <main className="public-shell">
        <p>Loading booking…</p>
      </main>
    );
  if (booking.error)
    return (
      <main className="public-shell">
        <Card>
          <h1>Booking access unavailable</h1>
          <p className="error">The secure link is invalid, expired, or revoked.</p>
        </Card>
      </main>
    );
  const item = booking.data!;
  return (
    <main className="public-shell">
      <header className="public-header">
        <span className="eyebrow">Booking management</span>
        <h1>{item.service.name}</h1>
      </header>
      <div className="booking-layout">
        <Card>
          <h2>Booking summary</h2>
          <dl className="summary-list">
            <div>
              <dt>Status</dt>
              <dd>{item.status.replaceAll('_', ' ')}</dd>
            </div>
            <div>
              <dt>When</dt>
              <dd>{new Date(item.startsAt).toLocaleString()}</dd>
            </div>
            <div>
              <dt>Location</dt>
              <dd>{item.location.name}</dd>
            </div>
            <div>
              <dt>Staff</dt>
              <dd>{item.staff.displayName}</dd>
            </div>
            <div>
              <dt>Total</dt>
              <dd>
                {(item.service.grossAmountMinor / 100).toLocaleString(undefined, {
                  style: 'currency',
                  currency: item.service.currency
                })}
              </dd>
            </div>
            <div>
              <dt>Paid</dt>
              <dd>
                {(item.payment.paidMinor / 100).toLocaleString(undefined, {
                  style: 'currency',
                  currency: item.payment.currency
                })}
              </dd>
            </div>
            <div>
              <dt>Balance</dt>
              <dd>
                {(item.payment.outstandingMinor / 100).toLocaleString(undefined, {
                  style: 'currency',
                  currency: item.payment.currency
                })}
              </dd>
            </div>
            <div>
              <dt>Payment</dt>
              <dd>{item.payment.state.replaceAll('_', ' ')}</dd>
            </div>
          </dl>
          {item.status === 'needs_review' ? (
            <div className="payment-placeholder">
              <strong>Payment requires assistance</strong>
              <p>A successful payment was preserved, but the booking needs staff review.</p>
            </div>
          ) : null}
          {error ? <p className="error">{error}</p> : null}
          {item.policy.cancellationAllowed &&
          ['awaiting_payment', 'confirmed'].includes(item.status) ? (
            <Button className="button-secondary" onClick={() => void cancel()}>
              Cancel booking
            </Button>
          ) : null}
        </Card>
        {item.status === 'awaiting_payment' ? (
          <PaymentPanel
            bookingId={item.id}
            token={token}
            options={item.paymentOptions}
            onProcessing={() =>
              void queryClient.invalidateQueries({ queryKey: ['managed-booking', token] })
            }
          />
        ) : null}
        {item.policy.rescheduleAllowed &&
        ['awaiting_payment', 'confirmed'].includes(item.status) ? (
          <Card>
            <h2>Choose a new time</h2>
            <form className="stack" onSubmit={(event) => void find(event)}>
              <label>
                Date
                <input type="date" value={selectedDate} onChange={(e) => setDate(e.target.value)} />
              </label>
              <Button>Find times</Button>
            </form>
            <div className="slot-grid">
              {slots.map((slot) => (
                <button
                  type="button"
                  className="slot"
                  key={`${slot.staff_id}-${slot.starts_at}`}
                  onClick={() => void move(slot)}
                >
                  {slot.local_starts_at.slice(11, 16)}
                  <small>{slot.time_zone}</small>
                </button>
              ))}
            </div>
          </Card>
        ) : null}
      </div>
    </main>
  );
}
